import { Component, EventEmitter, Output, Input } from '@angular/core';

@Component({
    selector: 'unsaved-changes-dialog',
    templateUrl: './unsaved-changes-dialog.component.html',
    styleUrls: ['./unsaved-changes-dialog.component.scss'],
    standalone: true,
})
export class UnsavedChangesDialogComponent {
    /**
     * Emits when the user chooses to continue without saving changes. Note that this does not actually perform any navigation action, it just indicates the user's choice. The parent component or guard is responsible for handling the navigation logic when this event is emitted.
     * This is different from the dismiss event, which is just closing the dialog without explicitly choosing to continue or save. In the context of a navigation guard, cancel would allow navigation to proceed, while dismiss would keep the user on the current page.
     */
    @Output() cancel = new EventEmitter<void>();

    /** 
     * Emits when the user chooses to save changes. Note that this does not actually perform the save action, it just indicates the user's choice. The parent component is responsible for handling the save logic when this event is emitted.
    */
    @Output() confirm = new EventEmitter<void>();

    /**
     * Emits when the user closes the dialog without choosing Save or Continue.
     * Used to cancel pending navigation when the close icon is clicked.
     */
    @Output() dismiss = new EventEmitter<void>();
    @Input() confirmDisabled = false;

    showDialog = false;
    title = 'Changes not saved';
    message =
        'The changes you have made to auto responses have not been saved.\nWould you like to save before leaving this page?';
    cancelButtonText = 'Continue without saving';
    confirmButtonText = 'SAVE';
    showClose = true;
    showCancel = true;
    showConfirm = true;
    preserveLineBreaks = true;

    open(
        title?: string,
        message?: string,
        options?: {
            cancelButtonText?: string;
            confirmButtonText?: string;
            showCancel?: boolean;
            showConfirm?: boolean;
            showClose?: boolean;
            preserveLineBreaks?: boolean;
        },
    ): void {
        this.title = title ?? this.title;
        this.message = message ?? this.message;
        this.cancelButtonText = options?.cancelButtonText ?? this.cancelButtonText;
        this.confirmButtonText = options?.confirmButtonText ?? this.confirmButtonText;
        this.showCancel = options?.showCancel ?? this.showCancel;
        this.showConfirm = options?.showConfirm ?? this.showConfirm;
        this.showClose = options?.showClose ?? this.showClose;
        this.preserveLineBreaks = options?.preserveLineBreaks ?? this.preserveLineBreaks;
        this.showDialog = true;
        this.preventBodyScroll();
    }

    /* 
     * Closes the dialog and restores body scroll.
     */
    close(): void {
        this.showDialog = false;
        this.allowBodyScroll();
    }
    /* 
     * Emits the dismiss event and closes the dialog.
     */
    closeAction(): void {
        this.dismiss.emit();
        this.close();
    }

    /* 
     * Emits the cancel event and closes the dialog.
     */
    cancelAction(): void {
        this.cancel.emit();
        this.close();
    }

    /* 
     * Emits the confirm event and closes the dialog.
     */
    confirmAction(): void {
        if (this.confirmDisabled) {
            return;
        }
        this.confirm.emit();
        this.close();
    }

    private preventBodyScroll() {
        document.getElementsByTagName('body')[0].classList.add('body--fixed');
    }

    private allowBodyScroll() {
        document.getElementsByTagName('body')[0].classList.remove('body--fixed');
    }
}
